import React, { useState, useRef, useCallback, useEffect } from 'react';
import { X, Monitor, ArrowDownToLine, Sparkles, Zap, Radio, Shield } from 'lucide-react';
import { isMacOS, getDownloadUrl } from '@/utils/platformUtils';
import { skylarqBrand as brand } from '@/styles/tokens';
import skylarqLogo from '@/assets/skylarq_logo_small.png';
import { useAuth } from '@/contexts/AuthContext';

// Session-only persistence — banner always shows on new session
const SESSION_KEY = 'skylarq_desktop_banner_minimized';

const wasMinimizedThisSession = (): boolean => {
  return sessionStorage.getItem(SESSION_KEY) === 'true';
};

const features = [
  { icon: Zap, text: 'AI-powered lead generation' },
  { icon: Radio, text: 'Real-time company monitoring' },
  { icon: Shield, text: 'Meeting notes & transcription' },
];

interface DesktopDownloadBannerProps {
  /** Whether the chat panel is currently open (dataset view only) */
  isChatOpen?: boolean;
  /** Whether we're on a dataset view (has chat button) */
  isDatasetView?: boolean;
}

export const DesktopDownloadBanner: React.FC<DesktopDownloadBannerProps> = ({
  isChatOpen = false,
  isDatasetView = false,
}) => {
  const { userProfile, isAuthenticated } = useAuth();
  const [minimized, setMinimized] = useState(wasMinimizedThisSession());
  const [animatingOut, setAnimatingOut] = useState(false);
  const [ctaHover, setCtaHover] = useState(false);
  const mac = isMacOS();

  // Draggable icon state (session-only, resets to smart default on reload)
  const iconRef = useRef<HTMLDivElement>(null);
  const dragState = useRef({ isDragging: false, startX: 0, startY: 0, hasMoved: false });
  const [iconPos, setIconPos] = useState<{ x: number; y: number } | null>(null);

  // Default position based on layout context
  const getDefaultPos = useCallback(() => {
    const margin = 24;
    const bottom = margin;
    const right = isDatasetView && !isChatOpen ? 112 : margin;
    const rightOpen = isDatasetView && isChatOpen ? 352 : right;
    return {
      x: window.innerWidth - 48 - rightOpen,
      y: window.innerHeight - 48 - bottom,
    };
  }, [isDatasetView, isChatOpen]);

  // Clamp position to viewport
  const clamp = useCallback((pos: { x: number; y: number }) => ({
    x: Math.max(8, Math.min(pos.x, window.innerWidth - 56)),
    y: Math.max(8, Math.min(pos.y, window.innerHeight - 56)),
  }), []);

  // Reset to default position when layout context changes (unless user is actively dragging)
  useEffect(() => {
    if (!dragState.current.isDragging) {
      setIconPos(null);
    }
  }, [isDatasetView, isChatOpen]);

  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    dragState.current = { isDragging: true, startX: e.clientX, startY: e.clientY, hasMoved: false };
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  }, []);

  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    if (!dragState.current.isDragging) return;
    const dx = e.clientX - dragState.current.startX;
    const dy = e.clientY - dragState.current.startY;
    if (Math.abs(dx) > 3 || Math.abs(dy) > 3) {
      dragState.current.hasMoved = true;
    }
    if (!dragState.current.hasMoved) return;

    setIconPos(prev => {
      const base = prev || getDefaultPos();
      return clamp({ x: base.x + dx, y: base.y + dy });
    });
    dragState.current.startX = e.clientX;
    dragState.current.startY = e.clientY;
  }, [getDefaultPos, clamp]);

  const handlePointerUp = useCallback(() => {
    dragState.current.isDragging = false;
  }, []);

  const handleMinimize = () => {
    setAnimatingOut(true);
    setTimeout(() => {
      setMinimized(true);
      setAnimatingOut(false);
      sessionStorage.setItem(SESSION_KEY, 'true');
      setIconPos(null); // Reset to smart default
    }, 300);
  };

  const handleReopen = () => {
    if (dragState.current.hasMoved) return; // Don't open if was a drag
    setMinimized(false);
    sessionStorage.removeItem(SESSION_KEY);
  };

  // Hide only for authenticated users who have completed onboarding.
  // Unauthenticated users still see it as a nudge to download the app.
  if (isAuthenticated && userProfile?.onboarding_completed) {
    return null;
  }

  // Minimized draggable floating icon
  if (minimized) {
    const pos = iconPos || getDefaultPos();

    return (
      <div
        ref={iconRef}
        className="fixed z-50 group select-none touch-none"
        style={{
          left: pos.x,
          top: pos.y,
          cursor: dragState.current.isDragging ? 'grabbing' : 'grab',
        }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
      >
        <button
          onClick={handleReopen}
          className="relative flex items-center justify-center w-12 h-12 transition-transform hover:scale-110"
          style={{
            background: `linear-gradient(135deg, ${brand.accent}, ${brand.accentHover})`,
            borderRadius: '50%',
            boxShadow: '0 4px 20px rgba(196,93,53,0.35), 0 0 0 2px rgba(255,255,255,0.15)',
          }}
          aria-label="Open download banner"
        >
          <ArrowDownToLine className="w-5 h-5 text-white" />
          {/* Subtle pulse ring */}
          <span
            className="absolute inset-0 rounded-full animate-ping pointer-events-none"
            style={{
              backgroundColor: brand.accent,
              opacity: 0.15,
              animationDuration: '3s',
            }}
          />
        </button>
        {/* Tooltip on hover */}
        <div
          className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 px-3 py-1.5 rounded-lg text-[12px] font-medium text-white whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity"
          style={{ backgroundColor: brand.text }}
        >
          Download desktop app
        </div>
      </div>
    );
  }

  // Full modal overlay
  return (
    <div
      className="fixed inset-0 z-[115] flex items-center justify-center"
      style={{
        animation: animatingOut
          ? 'bannerFadeOut 0.3s ease-in forwards'
          : 'bannerFadeIn 0.3s ease-out forwards',
      }}
    >
      {/* Backdrop - clicking it minimizes */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={handleMinimize}
      />

      <div
        className="relative w-full max-w-lg mx-4 overflow-hidden"
        style={{
          backgroundColor: brand.bg,
          borderRadius: '20px',
          boxShadow: '0 32px 100px rgba(0,0,0,0.15), 0 0 0 1px rgba(0,0,0,0.06)',
          animation: animatingOut
            ? 'bannerScaleOut 0.3s ease-in forwards'
            : 'bannerScaleIn 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards',
        }}
      >
        {/* Close button - minimizes to icon */}
        <button
          onClick={handleMinimize}
          className="absolute top-4 right-4 p-2 rounded-lg transition-colors z-10"
          style={{ color: brand.textFaint }}
          onMouseEnter={(e) => (e.currentTarget.style.color = brand.textSoft)}
          onMouseLeave={(e) => (e.currentTarget.style.color = brand.textFaint)}
          aria-label="Close banner"
        >
          <X className="w-5 h-5" />
        </button>



        {/* Radial glow */}
        <div
          className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-48 pointer-events-none"
          style={{
            background: 'radial-gradient(ellipse at 50% -20%, rgba(196,93,53,0.15), transparent 70%)',
          }}
        />

        <div className="relative px-8 pt-8 pb-8">
          {/* Logo + badge */}
          <div className="flex items-center gap-3 mb-6">
            <img src={skylarqLogo} alt="Skylarq" className="w-12 h-12 object-contain" />
            <div
              className="inline-flex items-center gap-1.5 px-3 py-1.5"
              style={{
                backgroundColor: brand.accentGlow,
                borderRadius: '100px',
              }}
            >
              <Sparkles className="w-3.5 h-3.5" style={{ color: brand.accent }} />
              <span
                className="text-[12px] font-semibold uppercase tracking-wide"
                style={{ color: brand.accent }}
              >
                AI sales agent for Mac
              </span>
            </div>
          </div>

          {/* Headline */}
          <h2
            className="text-[28px] font-bold mb-3 leading-tight"
            style={{ color: brand.text }}
          >
            Skylarq is now a desktop app
          </h2>

          {/* Subtext — matches homepage hero */}
          <p
            className="text-[16px] mb-6 leading-relaxed"
            style={{ color: brand.textMuted }}
          >
            {mac
              ? 'Find leads. Run outreach. Transcribe meetings. Track signals. Close deals. One agent — fully automated.'
              : 'The Skylarq desktop app is currently available for macOS. A Windows version is coming soon — continue using the web app in the meantime.'}
          </p>

          {/* Feature list */}
          {mac && (
            <div className="flex flex-col gap-3 mb-8">
              {features.map((f, i) => {
                const Icon = f.icon;
                return (
                  <div key={i} className="flex items-center gap-3">
                    <div
                      className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: brand.accentGlow }}
                    >
                      <Icon className="w-4 h-4" style={{ color: brand.accent }} />
                    </div>
                    <span className="text-[15px] font-medium" style={{ color: brand.textSoft }}>
                      {f.text}
                    </span>
                  </div>
                );
              })}
            </div>
          )}

          {/* CTA */}
          {mac ? (
            <a
              href={getDownloadUrl()}
              className="flex items-center justify-center gap-2.5 w-full py-4 text-white text-[17px] font-semibold transition-all"
              style={{
                backgroundColor: ctaHover ? brand.accentHover : brand.accent,
                borderRadius: '100px',
                boxShadow: ctaHover ? brand.accentShadow : '0 8px 32px rgba(196,93,53,0.25)',
              }}
              onMouseEnter={() => setCtaHover(true)}
              onMouseLeave={() => setCtaHover(false)}
            >
              <ArrowDownToLine className="w-5 h-5" />
              Download for Mac
            </a>
          ) : (
            <button
              onClick={handleMinimize}
              className="flex items-center justify-center gap-2.5 w-full py-4 text-[17px] font-medium transition-colors"
              style={{
                backgroundColor: brand.bgShifted,
                color: brand.textSoft,
                borderRadius: '100px',
                border: `1px solid ${brand.border}`,
              }}
            >
              <Monitor className="w-5 h-5" />
              Continue with web app
            </button>
          )}

          <p className="text-center text-[12px] mt-4" style={{ color: brand.textFaint }}>
            {mac
              ? 'Mac only. Free during early access — no credit card required.'
              : 'Windows version coming soon.'}
          </p>
        </div>
      </div>

      {/* Keyframe animations */}
      <style>{`
        @keyframes bannerFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes bannerFadeOut {
          from { opacity: 1; }
          to { opacity: 0; }
        }
        @keyframes bannerScaleIn {
          from { opacity: 0; transform: scale(0.9) translateY(20px); }
          to { opacity: 1; transform: scale(1) translateY(0); }
        }
        @keyframes bannerScaleOut {
          from { opacity: 1; transform: scale(1) translateY(0); }
          to { opacity: 0; transform: scale(0.5) translateY(100px); }
        }
      `}</style>
    </div>
  );
};
